import { add, differenceInCalendarDays, differenceInDays, differenceInHours, differenceInMilliseconds, differenceInMinutes, differenceInMonths, differenceInSeconds, differenceInWeeks, endOfDay, endOfMonth, endOfWeek, endOfYear, format as dateFnsFormat, isAfter as dateFnsIsAfter, isBefore as dateFnsIsBefore, isSameDay as dateFnsIsSameDay, isValid as dateFnsIsValid, parse as dateFnsParse, parseISO, parseJSON, startOfDay, startOfMonth, startOfWeek, startOfYear } from 'date-fns';
import { enUS, ko } from 'date-fns/locale';

const root = typeof window !== 'undefined' ? window : globalThis;

const locales = {
    ko,
    'ko-KR': ko,
    en: enUS,
    'en-US': enUS,
};

const formatTokenMap = {
    YYYY: 'yyyy',
    YY: 'yy',
    DDDD: 'DDD',
    DDD: 'D',
    DD: 'dd',
    D: 'd',
    A: 'a',
};

const supportedLegacyFormatTokens = /YYYY|YY|DDDD|DDD|DD|D|A/g;

function resolveLocale(locale) {
    return locales[locale] || ko;
}

function momentLiteralToDateFns(literal) {
    return "'" + literal.slice(1, -1).replace(/'/g, "''") + "'";
}

function normalizeFormatPattern(pattern) {
    if (!pattern) {
        return 'yyyy-MM-dd';
    }

    return String(pattern)
        .replace(/\[[^\]]*]/g, momentLiteralToDateFns)
        .replace(supportedLegacyFormatTokens, function (token) {
            return formatTokenMap[token] || token;
        });
}

function isValidDate(date) {
    return date instanceof Date && dateFnsIsValid(date);
}

function parseWithPattern(value, pattern, options = {}) {
    try {
        const parsed = dateFnsParse(
            value,
            normalizeFormatPattern(pattern),
            options.referenceDate || new Date(),
            { locale: resolveLocale(options.locale) }
        );

        return isValidDate(parsed) ? parsed : null;
    } catch (_error) {
        return null;
    }
}

function toDate(value, options = {}) {
    if (value instanceof Date) {
        return new Date(value.getTime());
    }

    if (value && value._skmDateCursor && typeof value.toDate === 'function') {
        return value.toDate();
    }

    if (typeof value === 'number') {
        const date = new Date(value);
        return isValidDate(date) ? date : null;
    }

    if (value && typeof value === 'object') {
        const now = new Date();
        const year = Number(value.year ?? now.getFullYear());
        const month = Number(value.month ?? 0);
        const date = Number(value.date ?? 1);
        const hours = Number(value.hours ?? value.hour ?? 0);
        const minutes = Number(value.minutes ?? value.minute ?? 0);
        const seconds = Number(value.seconds ?? value.second ?? 0);
        const milliseconds = Number(value.milliseconds ?? value.millisecond ?? 0);
        const objectDate = new Date(year, month, date, hours, minutes, seconds, milliseconds);

        return isValidDate(objectDate) ? objectDate : null;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    if (options.parsePattern) {
        const parsed = parseWithPattern(trimmed, options.parsePattern, options);
        if (parsed) {
            return parsed;
        }
    }

    const isoDate = parseISO(trimmed);
    if (isValidDate(isoDate)) {
        return isoDate;
    }

    const jsonDate = parseJSON(trimmed);
    if (isValidDate(jsonDate)) {
        return jsonDate;
    }

    const nativeDate = new Date(trimmed);
    return isValidDate(nativeDate) ? nativeDate : null;
}

function addByUnit(value, amount, unit = 'days') {
    const date = toDate(value);
    if (!date) {
        return null;
    }

    const normalizedUnit = String(unit).toLowerCase();
    const duration = {};

    if (['millisecond', 'milliseconds', 'ms'].includes(normalizedUnit)) {
        duration.milliseconds = amount;
    } else if (['second', 'seconds', 's'].includes(normalizedUnit)) {
        duration.seconds = amount;
    } else if (['minute', 'minutes', 'm'].includes(normalizedUnit)) {
        duration.minutes = amount;
    } else if (['hour', 'hours', 'h'].includes(normalizedUnit)) {
        duration.hours = amount;
    } else if (['week', 'weeks', 'w'].includes(normalizedUnit)) {
        duration.weeks = amount;
    } else if (unit === 'M' || ['month', 'months'].includes(normalizedUnit)) {
        duration.months = amount;
    } else if (['year', 'years', 'y'].includes(normalizedUnit)) {
        duration.years = amount;
    } else {
        duration.days = amount;
    }

    return add(date, duration);
}

function normalizeAmountAndUnit(first, second) {
    if (typeof first === 'string') {
        return { amount: Number(second || 0), unit: first };
    }

    return { amount: Number(first || 0), unit: second || 'days' };
}

function diffByUnit(a, b, unit = 'milliseconds') {
    const dateA = toDate(a);
    const dateB = toDate(b);

    if (!dateA || !dateB) {
        return 0;
    }

    const normalizedUnit = String(unit).toLowerCase();

    if (['day', 'days', 'd'].includes(normalizedUnit)) {
        return differenceInDays(dateA, dateB);
    }

    if (['calendarDay', 'calendarDays'].includes(unit)) {
        return differenceInCalendarDays(dateA, dateB);
    }

    if (['hour', 'hours', 'h'].includes(normalizedUnit)) {
        return differenceInHours(dateA, dateB);
    }

    if (['minute', 'minutes', 'm'].includes(normalizedUnit)) {
        return differenceInMinutes(dateA, dateB);
    }

    if (['second', 'seconds', 's'].includes(normalizedUnit)) {
        return differenceInSeconds(dateA, dateB);
    }

    if (['week', 'weeks', 'w'].includes(normalizedUnit)) {
        return differenceInWeeks(dateA, dateB);
    }

    if (['month', 'months'].includes(normalizedUnit) || unit === 'M') {
        return differenceInMonths(dateA, dateB);
    }

    return differenceInMilliseconds(dateA, dateB);
}

function startOf(value, unit = 'day') {
    const date = toDate(value);
    if (!date) {
        return null;
    }

    const normalizedUnit = String(unit).toLowerCase();

    if (unit === 'M' || ['month', 'months'].includes(normalizedUnit)) {
        return startOfMonth(date);
    }

    if (['week', 'weeks', 'w'].includes(normalizedUnit)) {
        return startOfWeek(date, { locale: ko });
    }

    if (['year', 'years', 'y'].includes(normalizedUnit)) {
        return startOfYear(date);
    }

    return startOfDay(date);
}

function endOf(value, unit = 'day') {
    const date = toDate(value);
    if (!date) {
        return null;
    }

    const normalizedUnit = String(unit).toLowerCase();

    if (unit === 'M' || ['month', 'months'].includes(normalizedUnit)) {
        return endOfMonth(date);
    }

    if (['week', 'weeks', 'w'].includes(normalizedUnit)) {
        return endOfWeek(date, { locale: ko });
    }

    if (['year', 'years', 'y'].includes(normalizedUnit)) {
        return endOfYear(date);
    }

    return endOfDay(date);
}

function durationToMilliseconds(value, unit = 'milliseconds') {
    const amount = Number(value || 0);
    const normalizedUnit = String(unit).toLowerCase();

    if (['second', 'seconds', 's'].includes(normalizedUnit)) {
        return amount * 1000;
    }

    if (['minute', 'minutes', 'm'].includes(normalizedUnit)) {
        return amount * 60 * 1000;
    }

    if (['hour', 'hours', 'h'].includes(normalizedUnit)) {
        return amount * 60 * 60 * 1000;
    }

    if (['day', 'days', 'd'].includes(normalizedUnit)) {
        return amount * 24 * 60 * 60 * 1000;
    }

    return amount;
}

function durationParts(value, unit = 'milliseconds') {
    const totalMilliseconds = Math.max(0, durationToMilliseconds(value, unit));
    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { totalMilliseconds, totalSeconds, hours, minutes, seconds };
}

function formatDuration(value, unit = 'milliseconds') {
    const parts = durationParts(value, unit);
    const result = [];

    if (parts.hours > 0) {
        result.push(parts.hours + '시간');
    }

    if (parts.minutes > 0) {
        result.push(parts.minutes + '분');
    }

    if (parts.seconds > 0 || result.length === 0) {
        result.push(parts.seconds + '초');
    }

    return result.join(' ');
}

function isSame(a, b, unit = 'milliseconds') {
    const dateA = toDate(a);
    const dateB = toDate(b);

    if (!dateA || !dateB) {
        return false;
    }

    const normalizedUnit = String(unit).toLowerCase();

    if (['day', 'days', 'd'].includes(normalizedUnit)) {
        return dateFnsIsSameDay(dateA, dateB);
    }

    if (unit === 'M' || ['month', 'months'].includes(normalizedUnit)) {
        return dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth();
    }

    if (['year', 'years', 'y'].includes(normalizedUnit)) {
        return dateA.getFullYear() === dateB.getFullYear();
    }

    return dateA.getTime() === dateB.getTime();
}

function setDateUnit(value, unit, amount) {
    const date = toDate(value);
    if (!date) {
        return null;
    }

    const normalizedUnit = String(unit).toLowerCase();
    const numericAmount = Number(amount);

    if (['year', 'years', 'y'].includes(normalizedUnit)) {
        date.setFullYear(numericAmount);
    } else if (unit === 'M' || ['month', 'months'].includes(normalizedUnit)) {
        date.setMonth(numericAmount);
    } else if (['date', 'dayOfMonth'].includes(unit)) {
        date.setDate(numericAmount);
    } else if (['hour', 'hours', 'h'].includes(normalizedUnit)) {
        date.setHours(numericAmount);
    } else if (['minute', 'minutes', 'm'].includes(normalizedUnit)) {
        date.setMinutes(numericAmount);
    } else if (['second', 'seconds', 's'].includes(normalizedUnit)) {
        date.setSeconds(numericAmount);
    } else if (['millisecond', 'milliseconds', 'ms'].includes(normalizedUnit)) {
        date.setMilliseconds(numericAmount);
    }

    return date;
}

function cursor(value, options = {}) {
    let date = toDate(value, options) || new Date();

    const cursorApi = {
        _skmDateCursor: true,
        toDate() {
            return new Date(date.getTime());
        },
        valueOf() {
            return date.getTime();
        },
        clone() {
            return cursor(date);
        },
        isValid() {
            return isValidDate(date);
        },
        format(pattern = 'YYYY-MM-DD', formatOptions = {}) {
            return api.format(date, pattern, formatOptions);
        },
        date(valueToSet) {
            if (valueToSet === undefined) {
                return date.getDate();
            }

            date.setDate(Number(valueToSet));
            return this;
        },
        day() {
            return date.getDay();
        },
        month(valueToSet) {
            if (valueToSet === undefined) {
                return date.getMonth();
            }

            date.setMonth(Number(valueToSet));
            return this;
        },
        year(valueToSet) {
            if (valueToSet === undefined) {
                return date.getFullYear();
            }

            date.setFullYear(Number(valueToSet));
            return this;
        },
        set(unit, valueToSet) {
            const nextDate = setDateUnit(date, unit, valueToSet);
            if (nextDate) {
                date = nextDate;
            }
            return this;
        },
        add(first, second) {
            const normalized = normalizeAmountAndUnit(first, second);
            date = addByUnit(date, normalized.amount, normalized.unit) || date;
            return this;
        },
        subtract(first, second) {
            const normalized = normalizeAmountAndUnit(first, second);
            date = addByUnit(date, -normalized.amount, normalized.unit) || date;
            return this;
        },
        startOf(unit = 'day') {
            date = startOf(date, unit) || date;
            return this;
        },
        endOf(unit = 'day') {
            date = endOf(date, unit) || date;
            return this;
        },
        diff(other, unit = 'milliseconds') {
            return diffByUnit(date, other, unit);
        },
        isSame(other, unit = 'milliseconds') {
            return isSame(date, other, unit);
        },
        isBefore(other) {
            const otherDate = toDate(other);
            return !!otherDate && dateFnsIsBefore(date, otherDate);
        },
        isAfter(other) {
            const otherDate = toDate(other);
            return !!otherDate && dateFnsIsAfter(date, otherDate);
        },
    };

    return cursorApi;
}

const api = {
    locales,
    normalizeFormatPattern,
    toDate,
    parse(value, pattern, options = {}) {
        if (pattern) {
            return parseWithPattern(value, pattern, options);
        }

        return toDate(value, options);
    },
    isValid(value, options = {}) {
        return !!toDate(value, options);
    },
    format(value, pattern = 'yyyy-MM-dd', options = {}) {
        const date = toDate(value, options);
        if (!date) {
            return options.fallback || '';
        }

        try {
            return dateFnsFormat(date, normalizeFormatPattern(pattern), {
                locale: resolveLocale(options.locale),
            });
        } catch (_error) {
            return options.fallback || '';
        }
    },
    formatDate(value, options = {}) {
        return this.format(value, options.pattern || 'yyyy-MM-dd', options);
    },
    formatDateTime(value, options = {}) {
        return this.format(value, options.pattern || 'yyyy-MM-dd HH:mm:ss', options);
    },
    formatTime(value, options = {}) {
        return this.format(value, options.pattern || 'HH:mm', options);
    },
    add(value, amount, unit = 'days') {
        return addByUnit(value, amount, unit);
    },
    subtract(value, amount, unit = 'days') {
        return addByUnit(value, -amount, unit);
    },
    addDays(value, amount) {
        return addByUnit(value, amount, 'days');
    },
    addHours(value, amount) {
        return addByUnit(value, amount, 'hours');
    },
    addMonths(value, amount) {
        return addByUnit(value, amount, 'months');
    },
    diff(a, b, unit = 'milliseconds') {
        return diffByUnit(a, b, unit);
    },
    diffDays(a, b) {
        return diffByUnit(a, b, 'days');
    },
    diffMinutes(a, b) {
        return diffByUnit(a, b, 'minutes');
    },
    startOf(value, unit = 'day') {
        return startOf(value, unit);
    },
    startOfMonth(value) {
        return startOf(value, 'month');
    },
    endOf(value, unit = 'day') {
        return endOf(value, unit);
    },
    endOfMonth(value) {
        return endOf(value, 'month');
    },
    set(value, unit, amount) {
        return setDateUnit(value, unit, amount);
    },
    day(value) {
        const date = toDate(value);
        return date ? date.getDay() : null;
    },
    isBefore(a, b) {
        const dateA = toDate(a);
        const dateB = toDate(b);
        return !!dateA && !!dateB && dateFnsIsBefore(dateA, dateB);
    },
    isAfter(a, b) {
        const dateA = toDate(a);
        const dateB = toDate(b);
        return !!dateA && !!dateB && dateFnsIsAfter(dateA, dateB);
    },
    isSame(a, b, unit = 'milliseconds') {
        return isSame(a, b, unit);
    },
    cursor(value, options = {}) {
        return cursor(value, options);
    },
    durationParts(value, unit = 'milliseconds') {
        return durationParts(value, unit);
    },
    formatDuration(value, unit = 'milliseconds') {
        return formatDuration(value, unit);
    },
    now() {
        return new Date();
    },
};

root.skmDate = Object.assign(root.skmDate || {}, api);

export default api;
